/**
 * Firmware Update Tool Page
 *
 * Upload and install firmware updates on IoT devices.
 * Tool color: orange
 *
 * Workflow:
 * 1. Select technology
 * 2. Manage serial ports and detect devices
 * 3. Upload firmware and view results
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  FolderDown,
  Loader2,
  Pause,
  Play,
  Plus,
  Settings,
  Upload,
  XCircle,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import {
  FirmwareUploadProgress,
  type FirmwareUploadStatus,
} from '@/components/production/FirmwareUploadProgress';
import { TechnologySearchForm } from '@/components/production/TechnologySearchForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFirmwareUpdate } from '@/hooks/production/useFirmwareUpdate';
import { cn } from '@/lib/utils';
import type { IFirmwareUpdateLog } from '@/types/production/firmware-update-log';

// ============================================
// Types
// ============================================

// ============================================
// Status Badge Component
// ============================================

function DeviceStatusBadge({
  status,
}: {
  status: 'fully_identified' | 'partially_identified' | 'not_identified';
}) {
  const config = {
    fully_identified: {
      label: 'Identificado',
      className: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    },
    partially_identified: {
      label: 'Parcialmente Identificado',
      className: 'bg-yellow-100 text-yellow-800',
      dotClass: 'bg-yellow-500',
    },
    not_identified: {
      label: 'Não Identificado',
      className: 'bg-red-100 text-red-800',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-2 w-2 rounded-full', dotClass)} />
      <span className={cn('text-xs font-medium', className)}>{label}</span>
    </div>
  );
}

// ============================================
// Detected Devices Table
// ============================================

function DetectedDevicesTable({
  devices,
}: {
  devices: Array<{ port: any; equipment?: any; status: any }>;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum dispositivo detectado</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Conecte uma porta serial e aguarde a detecção automática
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>IMEI</TableHead>
            <TableHead>ICCID</TableHead>
            <TableHead>Firmware Atual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device, index) => (
            <TableRow key={device.equipment?.serial || index}>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.serial ?? '--'}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.imei ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.iccid ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.firmware ?? '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Updated Devices Table
// ============================================

function UpdatedDevicesTable({ devices }: { devices: IFirmwareUpdateLog[] }) {
  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          Nenhum equipamento gravado
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Complete as etapas anteriores para visualizar os equipamentos gravados.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Resultado</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>Firmware</TableHead>
            <TableHead>Data</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => (
            <TableRow key={device.id}>
              <TableCell>
                {device.status ? (
                  <Badge variant="success-subtle" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Atualizado
                  </Badge>
                ) : (
                  <Badge variant="destructive-subtle" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Falhou
                  </Badge>
                )}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment.serial}</TableCell>
              <TableCell className="font-mono text-sm text-black font-semibold">
                {device.equipment.firmware}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(device.created_at).toLocaleString('pt-BR')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function FirmwareUpdatePage() {
  const [searchParams] = useSearchParams();
  const technologyId = searchParams.get('technology_id');

  // State
  const [selectedTechnology, setSelectedTechnology] = React.useState<ITechnology | null>(null);
  const [consoleLogs, setConsoleLogs] = React.useState<string[]>([]);
  const [firmwareFile, setFirmwareFile] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadStatus, setUploadStatus] = React.useState<FirmwareUploadStatus>('idle');

  // File input ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch technology if ID is in URL
  const { data: technologyData } = useQuery({
    queryKey: ['technology', technologyId],
    queryFn: () => technologyApi.findOne(technologyId!),
    enabled: !!technologyId,
  });

  // Set technology from URL param
  React.useEffect(() => {
    if (technologyData) {
      setSelectedTechnology(technologyData);
    }
  }, [technologyData]);

  const date = new Date();

  // Add log helper
  const addLog = React.useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setConsoleLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  // Clear logs
  const clearLogs = React.useCallback(() => {
    setConsoleLogs([]);
  }, []);

  // Helper to format response values for logging
  const formatResponseValue = React.useCallback((value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.join(',');
    }
    if (typeof value === 'object') {
      // Try to extract meaningful values from the object
      const values = Object.values(value).filter((v) => v !== null && v !== undefined);
      if (values.length === 1 && typeof values[0] === 'string') {
        return values[0];
      }
      if (values.length === 1) {
        return String(values[0]);
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  // Handle technology change
  const handleTechnologyChange = React.useCallback(
    (tech: ITechnology | null) => {
      setSelectedTechnology(tech);
      setFirmwareFile(null);
      setUploadProgress(0);
      setUploadStatus('idle');
      clearLogs();
      if (tech) {
        addLog(`Tecnologia selecionada: ${tech.name.brand}`);
      }
    },
    [addLog, clearLogs],
  );

  // Handle file selection
  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setFirmwareFile(file);
        setUploadProgress(0);
        setUploadStatus('idle');
        addLog(`Arquivo selecionado: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
      }
    },
    [addLog],
  );

  // Use the firmware update hook
  const { updated, detected, update, requestPort, isUpdating, isDetecting, toDetect, setToDetect } =
    useFirmwareUpdate({
      technology: selectedTechnology,
    });

  // Log when devices are detected
  const detectedSerialsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const currentSerials = new Set(
      detected.filter((d) => d.equipment?.serial).map((d) => d.equipment?.serial!),
    );
    const newSerials = Array.from(currentSerials).filter(
      (serial) => !detectedSerialsRef.current.has(serial),
    );

    newSerials.forEach((serial) => {
      addLog(`Dispositivo detectado: ${serial}`);
    });

    detectedSerialsRef.current = currentSerials;

    if (detected.length === 0 && detectedSerialsRef.current.size > 0) {
      addLog('Nenhum dispositivo detectado');
      detectedSerialsRef.current.clear();
    }
  }, [detected, addLog]);

  // Log when firmware update completes
  const lastUpdatedLengthRef = React.useRef(0);
  React.useEffect(() => {
    const newCount = updated.length - lastUpdatedLengthRef.current;
    if (newCount > 0) {
      const newDevices = updated.slice(-newCount);
      newDevices.forEach((device) => {
        if (device.status) {
          addLog(`✓ ${device.equipment.serial} atualizado com sucesso`);
          addLog(`  Firmware: ${device.equipment.firmware}`);
          // Log commands sent and responses received
          device.messages?.forEach((msg) => {
            addLog(`  → ${msg.key}: ${msg.request}`);
            if (msg.response) {
              const responseStr = formatResponseValue(msg.response);
              if (responseStr) {
                addLog(`  ← ${responseStr}`);
              }
            }
          });
        } else {
          addLog(`✗ Falha ao atualizar ${device.equipment.serial}`);
        }
      });
    }
    lastUpdatedLengthRef.current = updated.length;
  }, [updated, addLog, formatResponseValue]);

  // Update firmware with logging
  const handleUpdate = React.useCallback(async () => {
    if (!firmwareFile) return;

    addLog(`Iniciando atualização de firmware: ${firmwareFile.name}`);
    addLog(`Atualizando ${detected.length} dispositivo(s)`);
    await update(detected);
    setUploadProgress(100);
    setUploadStatus('complete');
  }, [update, detected, firmwareFile, addLog]);

  // Toggle detection
  const toggleDetection = React.useCallback(() => {
    setToDetect(!toDetect);
    addLog(toDetect ? 'Detecção pausada' : 'Detecção retomada');
  }, [toDetect, setToDetect, addLog]);

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">Gravação de Firmware</h1>
        <p className="mt-2 text-sm text-gray-700">
          Ferramenta para requisitar a atualização de firmware em{' '}
          <span className="text-gray-900">equipamentos IoT.</span> Data de hoje:{' '}
          <time dateTime={date.toLocaleDateString()}>{date.toLocaleDateString()}</time>
        </p>
      </div>

      {/* Step 1: Technology Selection */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant="default"
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              1
            </Badge>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Definição da tecnologia</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">Escolha a tecnologia dos equipamentos</CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          <TechnologySearchForm
            technology={selectedTechnology}
            showConfigurationProfile={false}
            showSerialNumber={false}
            onTechnologyChange={handleTechnologyChange}
          />

          {/* Firmware File Upload */}
          <div className="space-y-2">
            <Label>Arquivo de Firmware</Label>
            <div className="relative">
              <input
                id="firmware-file"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".bin,.hex,.fw"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'relative flex items-center justify-between rounded-lg border-2 border-dashed p-4 transition-all cursor-pointer w-full text-left',
                  firmwareFile
                    ? 'border-black bg-gray-50 hover:bg-gray-100'
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50',
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-lg',
                      firmwareFile ? 'bg-black' : 'bg-gray-100',
                    )}
                  >
                    <Upload
                      className={cn('h-5 w-5', firmwareFile ? 'text-white' : 'text-gray-600')}
                    />
                  </div>
                  <div className="flex flex-col">
                    {firmwareFile ? (
                      <>
                        <span className="text-sm font-medium text-gray-900">
                          {firmwareFile.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {(firmwareFile.size / 1024).toFixed(2)} KB
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-medium text-gray-900">
                          Selecionar arquivo
                        </span>
                        <span className="text-xs text-muted-foreground">.bin, .hex, .fw</span>
                      </>
                    )}
                  </div>
                </div>
                {firmwareFile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFirmwareFile(null);
                      setUploadProgress(0);
                      setUploadStatus('idle');
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="h-8 w-8 hover:bg-gray-200"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Clique para selecionar um arquivo de firmware (.bin, .hex, .fw)
            </p>
          </div>

          {/* Upload Progress */}
          {firmwareFile && (
            <FirmwareUploadProgress
              progress={uploadProgress}
              status={uploadStatus}
              fileName={firmwareFile.name}
            />
          )}
        </CardContent>
      </Card>

      {/* Step 2: Port Management */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Badge
                variant={selectedTechnology ? 'default' : 'secondary'}
                className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
              >
                2
              </Badge>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-black" />
                <CardTitle className="text-xl">Gerenciamento de Portas</CardTitle>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleDetection}
              className={cn(toDetect ? 'text-black hover:text-gray-800' : 'text-muted-foreground')}
            >
              {toDetect ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          </div>
          <CardDescription className="ml-11">
            Visualize os equipamentos conectados às portas seriais
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <DetectedDevicesTable devices={detected} />

            <Separator />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleUpdate}
                  disabled={!firmwareFile || detected.length === 0 || isDetecting || isUpdating}
                  className="min-w-[140px] bg-black hover:bg-gray-800 text-white"
                >
                  {isDetecting ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Detectando
                    </>
                  ) : isUpdating ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Atualizando
                    </>
                  ) : (
                    <>
                      <FolderDown className="mr-2 h-4 w-4" />
                      Atualizar
                    </>
                  )}
                </Button>

                {!firmwareFile && (
                  <p className="text-sm text-muted-foreground self-center">
                    Selecione um arquivo de firmware para continuar
                  </p>
                )}
              </div>

              <Button variant="outline" onClick={requestPort} className="min-w-[140px]">
                <Plus className="mr-2 h-4 w-4" />
                Nova Porta
              </Button>
            </div>
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos detectados.
            </p>
          </div>
        )}
      </Card>

      {/* Step 3: Results */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={updated.length > 0 ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              3
            </Badge>
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`h-5 w-5 ${updated.length > 0 ? 'text-black' : 'text-muted-foreground'}`}
              />
              <CardTitle className="text-xl">Verificação e Resultados</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            {updated.length > 0
              ? `${updated.length} equipamento(s) gravado(s) com sucesso`
              : 'Aguardando gravação dos equipamentos'}
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <UpdatedDevicesTable devices={updated} />

            {/* Console Output */}
            <ConsoleDisplay
              logs={consoleLogs}
              height={200}
              title="Log de Execução"
              textColor="amber"
              bgColor="gray"
              showLineNumbers={false}
              onClear={clearLogs}
            />
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos gravados.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

export { FirmwareUpdatePage };
